<template>
    <div>
        <div class="workorder_staff_wrapper"
             v-for="userPosition in userPositionsWithAssignedUsers"
             :key="userPosition.userPositionId">
            <label class="role_title">
                {{userPosition.userPositionId==enumUserPositions.master?translate('master'):userPosition.userPositionId==enumUserPositions.observer?translate('observer'):translate('assigned')}} {{translate('user')}}
                ({{userPosition.users.length}})
                <a v-if="isEditable" href="javascript:;"><i @click="$refs.assignUsersPanel.open()" class="fa fa-plus"></i></a>
            </label>
            <div class="staff_item"
                 v-tooltip="!user.IsActiveUser?translate('deleted_entry'):false"
                 :class="{'removed-item':!user.IsActiveUser?true:false}"
                 v-for="user in userPosition.users">
                <a v-if="isEditable && canRemoveAssignedUser(user)" class="pull-right" href="javascript:;"><i @click="removeUser(user)" class="fa fa-fw fa-times"></i></a>
                <!-- Kayitta calismis personel cikarilamaz; dugme yerine sebebini soyleyen kilit. -->
                <span v-else-if="isEditable" class="pull-right"
                      v-tooltip="translate('worked_staff_cannot_be_removed')"><i class="fa fa-fw fa-lock text-muted"></i></span>
                <user-card2 class="workorder_staff_card"
                            :user="user"
                            :locked="isLocked"
                            @save-spent-minutes="saveSpentMinutes">
                    {{user.NameSurname}}
                </user-card2>
            </div>
        </div>
        <!-- Duzenleme sagdan acilan panelde: bu ekranda yalnizca atanan personel degistirilir. -->
        <work-order-assign-users-panel v-if="isEditable"
                                       ref="assignUsersPanel"
                                       :workOrderId="workOrderId"
                                       :workOrderName="workOrderName"
                                       @saved="refresh"/>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import * as api from '../../../../../scripts/services/api';
import * as blockui from '../../../../../helpers/block-ui';
import UserCard2 from '../../../../partials/cards/UserCard2';
import WorkOrderAssignUsersPanel from '../../../../partials/modals/WorkOrderAssignUsersPanel';
import { groupBy } from '../../../../../scripts/global/util';
import { canRemoveAssignedUser } from '../../../../../scripts/services/workOrderStatusPolicy';
import { getWorkOrderLock } from '../../../../../scripts/services/workOrderLocks';

export default {
  name: 'WorkOrderAssignedUsers',
  components: {
    'user-card2': UserCard2,
    'work-order-assign-users-panel': WorkOrderAssignUsersPanel,
  },
  props: {
    workOrderId: {
      type: [Number, String],
      required: true,
    },
    isEditable: {
      type: [Boolean, String],
      required: false,
    },
    // Panelin basliginda gosterilir; atama yalnizca workOrderId ile yapiliyor.
    workOrderName: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      userPositionsWithAssignedUsers: [],
      // Kilit bayragi sunucudan gelen IsSpentMinutesEditable'in ICINDE degil - ayri bir kapi.
      // Servis 15 sn onbellekli ve ayni sayfadaki kilit seridi zaten doldurmus oluyor,
      // yani burada ek bir HTTP istegi olusmuyor.
      isLocked: false,
    };
  },
  async mounted() {
    blockui.blockElementPromise(this, this.$el);
    try {
      // Kilit yalnizca burada okunuyor: bir sure kaydi kilit durumunu degistirmez, ve ayni
      // sayfadaki kilit seridi servisin onbellegini zaten doldurmus oluyor (ek istek yok).
      this.isLocked = !!(await getWorkOrderLock(this.workOrderId));
      await this.initializeData();
    } catch (e) {
      toastr.error(e.message);
    } finally {
      blockui.unBlockElement(this.$el);
    }
  },
  methods: {
    async initializeData() {
      const response = await this.$globalApiClient.workOrder.workOrderGetAssignedUsers({workOrderId: this.workOrderId});
      if ( !response.data ) {
        throw new Error('Error');
      }
      this.userPositionsWithAssignedUsers = this.formatWorkOrderUserPositionData(response.data);
    },

    /**
     * Bir personelin harcadigi sureyi kaydeder.
     *
     * Kaydedilince is emrinin "Gercek Sure"si de degisir: sunucu, kisi bazli sure girilmis bir
     * is emrinde toplami bu satirlardan hesapliyor. Ayni toplami burada da cikarip karta
     * yayinliyoruz - ikinci bir istek gerekmiyor.
     */
    async saveSpentMinutes({ userId, spentMinutes }) {
      const self = this;
      blockui.blockElementPromise(self, self.$el);
      try {
        const response = await api.setWorkOrderUserSpentMinutes({
          WorkOrderId: self.workOrderId,
          UserId: userId,
          SpentMinutes: spentMinutes,
        });

        if (!response.body.IsSuccess) {
          toastr.error(response.body.Message);
          return;
        }

        toastr.success(self.translate('save_successfully'));
        await self.initializeData();
        self.$eventHub.$emit('workOrderSpentMinutesChanged', {
          workOrderId: Number(self.workOrderId),
          spentTimeMinutes: self.totalSpentMinutes(),
        });
      } catch (e) {
        toastr.error(e.message);
      } finally {
        blockui.unBlockElement(self.$el);
      }
    },

    /**
     * Atanmis personelin girilmis surelerinin toplami; hic girilmemisse null.
     *
     * KISI basina tekillestiriyor: sunucu sureyi kisiye gore grupluyor ve ayni kisinin HER
     * atama satirina ayni toplami yaziyor. Ayni kullanici iki pozisyonda iki kart olarak
     * gorunebildigi icin kartlari duz toplamak o kisinin dakikasini iki kez sayardi.
     */
    totalSpentMinutes() {
      const perUser = new Map();
      this.userPositionsWithAssignedUsers
        .reduce((all, position) => all.concat(position.users), [])
        .filter((user) => user.SpentMinutes !== null && user.SpentMinutes !== undefined)
        .forEach((user) => perUser.set(user.Id, user.SpentMinutes));

      return perUser.size === 0
        ? null
        : [...perUser.values()].reduce((sum, minutes) => sum + minutes, 0);
    },
    formatWorkOrderUserPositionData(assignedUsers) {
      const assignedUsersGroupedByUserPosition = groupBy(assignedUsers, (x) => x.UserPositionId);
      // Bilinen pozisyonlar bos olsalar da basilir: yoksa hic personeli olmayan bir kayitta
      // tiklanacak bir "+" kalmiyor. Listede olmayan bir pozisyon gelirse (BE'de Assistant=3 var,
      // FE enum'unda yok) o da eklenir.
      const userPositionIds = [...new Set([
        ...Object.values(this.enumUserPositions),
        ...assignedUsersGroupedByUserPosition.keys(),
      ])];
      return userPositionIds.map((userPositionId) => ({
        userPositionId,
        users: assignedUsersGroupedByUserPosition.get(userPositionId) || [],
      }));
    },
    async refresh() {
      try {
        await this.initializeData();
      } catch (e) {
        toastr.error(e.message);
      }
      this.$eventHub.$emit('reloadTabCounts', { workOrderId: this.workOrderId });
    },
    // Sablon yalnizca bilesen uyelerini gorebiliyor; politika fonksiyonu oldugu gibi kaydedilir.
    canRemoveAssignedUser,
    async removeUser(user) {
      const self = this;
      const formData = {
        workOrderId: user.WorkOrderId,
        userId: user.Id,
      };
      await api.deleteWorkOrderUsers(formData).then((response) => {
        if (response.body.IsSuccess) {
          toastr.success(self.translate('delete_successfully'));
          self.refresh();
        } else toastr.error(response.body.Message);
      }, (error) => {
      });
    },
  },
  computed: {
    ...mapState({
      enumUserPositions: (state) => state.enums.userPositions,
    }),
  },
};
</script>
